import React, { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { IconBuilding, IconUser } from '@tabler/icons-react';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import {
  triggerDomainSync,
  triggerDomainManage,
  fetchOwnerAudit,
} from '../services/domainManagerApi';
import type {
  SyncSummary,
  ManageSummary,
  OwnerAuditSummary,
  DetailRow,
} from '../services/domainManagerApi';
import { updateDomain } from '../services/domainsApi';
import { getCustomerIndexCached } from '../services/customerIndexCacheService';

// Friendly labels for the per-domain action types that appear in details.
const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  deactivate: 'Deactivate',
  without_owner: 'Without owner',
  notification: 'Notification',
  schedule_item: 'Schedule item',
  invoice: 'Invoice',
  autorenew_disabled: 'Auto-renew disabled',
  autorenew_enabled: 'Auto-renew enabled',
  techsolvers_flag: 'TechSolvers flag',
  lapsing: 'Lapsing',
  owner_disagree: 'Owners disagree',
  owner_string_only: 'Missing from domain row',
  owner_lookup_only: 'Missing from RS list',
};

const actionLabel = (action: string) => ACTION_LABELS[action] ?? action;

function renderChanges(row: DetailRow): React.ReactNode {
  if (row.changes && row.changes.length > 0) {
    return (
      <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
        {row.changes.map((c, i) => (
          <Box component="span" key={`${c.field}-${i}`} sx={{ display: 'block' }}>
            {c.field}: {String(c.from ?? '∅')} → {String(c.to ?? '∅')}
          </Box>
        ))}
      </Box>
    );
  }
  return row.detail || '—';
}

/**
 * Inline customer picker + Assign button rendered in the detail table for
 * `without_owner` rows. Lets an operator assign the unassigned domain to a
 * customer directly from the results — the assignment is an immediate write to
 * domain_manager (independent of the run's Dry Run toggle, just like the
 * auto-renew / action-on-expiry admin toggles). On success the control collapses
 * to a confirmation chip; the row will drop out of "without owner" on the next run.
 */
const AssignOwnerCell: React.FC<{
  domainId: string;
  options: CustomerIndexItemDto[];
  disabled?: boolean;
}> = ({ domainId, options, disabled }) => {
  const [selected, setSelected] = useState<CustomerIndexItemDto | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState<string>('');

  const optionLabel = (c: CustomerIndexItemDto) => c.businessName || c.displayName;

  const assign = async () => {
    if (!selected) return;
    setStatus('saving');
    setError(null);
    try {
      await updateDomain(domainId, { ownerCustomerGuid: selected.dataverseCustomerId });
      setAssignedTo(optionLabel(selected));
      setStatus('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assignment failed');
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        color="success"
        size="small"
        variant="outlined"
        label={`Assigned to ${assignedTo}`}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 340 }}>
      <Autocomplete<CustomerIndexItemDto>
        size="small"
        sx={{ flex: 1, minWidth: 220 }}
        options={options}
        value={selected}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(a, b) => a.dataverseCustomerId === b.dataverseCustomerId}
        filterOptions={(opts, state) => {
          const needle = state.inputValue.trim().toLowerCase();
          if (!needle) return opts.slice(0, 30);
          return opts
            .filter((c) =>
              [c.displayName, c.businessName ?? '', c.email ?? '']
                .join(' ')
                .toLowerCase()
                .includes(needle),
            )
            .slice(0, 50);
        }}
        onChange={(_, value) => setSelected(value)}
        disabled={disabled || status === 'saving'}
        renderOption={(props, option) => {
          const icon =
            option.customerType === 'Business' ? (
              <IconBuilding size={16} stroke={1.5} />
            ) : (
              <IconUser size={16} stroke={1.5} />
            );
          return (
            <li {...props} key={option.dataverseCustomerId}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  {icon}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 500 }}>
                    {optionLabel(option)}
                  </Typography>
                  {option.email && (
                    <Typography color="text.secondary" noWrap sx={{ fontSize: 11 }}>
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label="Assign to customer" placeholder="Find customer…" />
        )}
        blurOnSelect
      />
      <Button
        variant="contained"
        size="small"
        onClick={assign}
        disabled={!selected || disabled || status === 'saving'}
        startIcon={status === 'saving' ? <CircularProgress size={14} color="inherit" /> : undefined}
      >
        Assign
      </Button>
      {status === 'error' && error && (
        <Typography variant="caption" color="error" sx={{ maxWidth: 200 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

type RunKind = 'sync' | 'manage' | 'audit';

interface ResultState {
  kind: RunKind;
  message: string;
  summary: SyncSummary | ManageSummary | OwnerAuditSummary;
}

/** Renders a summary value: booleans as Yes/No, everything else as-is. */
const formatMetric = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value === undefined || value === null ? '—' : String(value);
};

const SYNC_FIELDS: { key: keyof SyncSummary; label: string }[] = [
  { key: 'vendor_count', label: 'Vendor domains' },
  { key: 'internal_count', label: 'Dataverse domains' },
  { key: 'skipped_vendor_rows', label: 'Skipped (non-OK)' },
  { key: 'would_upsert', label: 'Would upsert' },
  { key: 'upserted', label: 'Upserted' },
  { key: 'would_deactivate', label: 'Would deactivate' },
  { key: 'deactivated', label: 'Deactivated' },
];

const MANAGE_FIELDS: { key: keyof ManageSummary; label: string }[] = [
  { key: 'expiring_domains', label: 'Expiring domains' },
  { key: 'evaluated', label: 'Evaluated' },
  { key: 'without_owner', label: 'Without owner' },
  { key: 'techsolvers_skipped', label: 'TechSolvers skipped' },
  { key: 'techsolvers_flag_updated', label: 'TechSolvers flag corrected' },
  { key: 'lapsing_skipped', label: 'Lapsing (not renewed)' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'schedule_items_created', label: 'Schedule items' },
  { key: 'invoices_created', label: 'Invoices' },
  { key: 'autorenew_disabled', label: 'Auto-renew disabled' },
  { key: 'autorenew_enabled', label: 'Auto-renew enabled' },
];

const AUDIT_FIELDS: { key: keyof OwnerAuditSummary; label: string }[] = [
  { key: 'domain_count', label: 'Domains in Dataverse' },
  { key: 'inactive_skipped', label: 'Skipped (no longer held)' },
  { key: 'active_count', label: 'Active domains' },
  { key: 'lookup_owner', label: 'Owned via domain row' },
  { key: 'string_owner', label: 'Owned via RS list' },
  { key: 'lookup_coverage_pct', label: 'Domain-row coverage (%)' },
  { key: 'agree', label: 'Sources agree' },
  { key: 'disagree', label: 'Sources disagree' },
  { key: 'string_only', label: 'Needs backfill (RS list only)' },
  { key: 'lookup_only', label: 'Domain row only' },
  { key: 'no_owner', label: 'No owner in either' },
  { key: 'techsolvers_by_lookup', label: 'TechSolvers via domain row' },
  { key: 'techsolvers_by_string', label: 'TechSolvers via RS list' },
  { key: 'safe_to_cut_over', label: 'Safe to cut over' },
];

export const DomainManagerPage: React.FC = () => {
  const [running, setRunning] = useState<RunKind | false>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);

  const [syncDryRun, setSyncDryRun] = useState(true);
  const [manageDryRun, setManageDryRun] = useState(true);

  // Detail table: filter by action ('all' shows everything).
  const [metricFilter, setMetricFilter] = useState<string>('all');

  const abortRef = useRef<AbortController | null>(null);

  // Customer index backs the inline "assign owner" dropdown on without_owner rows.
  const [customerOptions, setCustomerOptions] = useState<CustomerIndexItemDto[]>([]);
  const [customerLoadError, setCustomerLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCustomerIndexCached()
      .then((res) => {
        if (!cancelled) setCustomerOptions(res.items);
      })
      .catch((err) => {
        if (!cancelled) {
          setCustomerLoadError(err instanceof Error ? err.message : 'Failed to load customers');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const run = async (kind: RunKind, dryRun: boolean) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setRunning(kind);
    setError(null);
    setResult(null);
    setMetricFilter('all');

    try {
      let data;
      if (kind === 'sync') {
        data = await triggerDomainSync(dryRun, controller.signal);
      } else if (kind === 'manage') {
        data = await triggerDomainManage(dryRun, controller.signal);
      } else {
        // The audit only reads, so it takes no dry-run flag.
        data = await fetchOwnerAudit(controller.signal);
      }
      setResult({ kind, message: data.message, summary: data.result });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : `${kind} run failed`);
    } finally {
      setRunning(false);
    }
  };

  const fields: { key: string; label: string }[] =
    result?.kind === 'sync'
      ? SYNC_FIELDS
      : result?.kind === 'audit'
        ? AUDIT_FIELDS
        : MANAGE_FIELDS;

  const details: DetailRow[] = result?.summary.details ?? [];
  const actionsPresent = Array.from(new Set(details.map((d) => d.action)));
  const visibleDetails =
    metricFilter === 'all' ? details : details.filter((d) => d.action === metricFilter);

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" gutterBottom>
        Domain Manager
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Operator triggers for the domain_manager service. Keep <strong>Dry Run</strong> on
        to preview what a run would do without writing to Synergy, Dataverse, or RepairShopr.
      </Typography>

      {/* Sync */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Sync — reconcile domain inventory (Synergy → Dataverse)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={syncDryRun}
                onChange={(e) => setSyncDryRun(e.target.checked)}
                disabled={running !== false}
              />
            }
            label="Dry Run"
          />
          <Button
            variant="contained"
            size="small"
            startIcon={running === 'sync' ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={() => run('sync', syncDryRun)}
            disabled={running !== false}
          >
            Run Sync
          </Button>
        </Box>
      </Paper>

      {/* Manage */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Manage — renewal billing lifecycle (expiring domains)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={manageDryRun}
                onChange={(e) => setManageDryRun(e.target.checked)}
                disabled={running !== false}
              />
            }
            label="Dry Run"
          />
          <Button
            variant="contained"
            size="small"
            startIcon={running === 'manage' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={() => run('manage', manageDryRun)}
            disabled={running !== false}
          >
            Run Manage
          </Button>
        </Box>
      </Paper>

      {/* Owner audit — read-only, so no Dry Run toggle */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Owner audit — compare ownership sources (read-only)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Checks how far each domain row's own customer lookup covers what the
          RepairShopr-sourced customer domain list claims, ahead of migrating ownership
          onto the domain row. Writes nothing.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={running === 'audit' ? <CircularProgress size={16} color="inherit" /> : <FactCheckIcon />}
          onClick={() => run('audit', false)}
          disabled={running !== false}
        >
          Run Owner Audit
        </Button>
      </Paper>

      {running && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {running === 'sync' ? 'Sync' : running === 'manage' ? 'Manage' : 'Owner audit'} run
          in progress…
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Box>
          <Alert severity="success" sx={{ mb: 1 }}>
            {result.message}
            {'dry_run' in result.summary && result.summary.dry_run && (
              <Chip label="DRY RUN" color="warning" size="small" sx={{ ml: 1 }} />
            )}
          </Alert>
          {result.kind === 'audit' && (
            <Alert
              severity={(result.summary as OwnerAuditSummary).safe_to_cut_over ? 'success' : 'warning'}
              sx={{ mb: 1 }}
            >
              {(result.summary as OwnerAuditSummary).safe_to_cut_over
                ? 'Domain-row ownership covers everything the RepairShopr list claims, with no ' +
                  'disagreements — the cutover can proceed.'
                : `Not ready to cut over: ${(result.summary as OwnerAuditSummary).disagree} ` +
                  `disagreement(s) and ${(result.summary as OwnerAuditSummary).string_only} ` +
                  'domain(s) owned only via the RepairShopr list, which a backfill would need to write.'}
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 480, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((f) => (
                  <TableRow key={f.key}>
                    <TableCell>{f.label}</TableCell>
                    <TableCell align="right">
                      {formatMetric((result.summary as Record<string, unknown>)[f.key])}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Detail table — what changes would be made, filterable by metric */}
          {details.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle2">
                  Changes ({visibleDetails.length}
                  {metricFilter !== 'all' ? ` of ${details.length}` : ''})
                </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="metric-filter-label">Filter by Metric</InputLabel>
                  <Select
                    labelId="metric-filter-label"
                    label="Filter by Metric"
                    value={metricFilter}
                    onChange={(e: SelectChangeEvent) => setMetricFilter(e.target.value)}
                  >
                    <MenuItem value="all">All ({details.length})</MenuItem>
                    {actionsPresent.map((a) => (
                      <MenuItem key={a} value={a}>
                        {actionLabel(a)} ({details.filter((d) => d.action === a).length})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {result.summary.details_truncated && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Detail list truncated — showing the first {details.length} changes. The counts
                  above reflect the full totals.
                </Alert>
              )}

              {customerLoadError && details.some((d) => d.action === 'without_owner') && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Couldn't load the customer list for owner assignment ({customerLoadError}).
                  Reload the page to try again.
                </Alert>
              )}

              <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 900 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell>Domain</TableCell>
                      <TableCell>Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleDetails.map((row, i) => (
                      <TableRow key={`${row.domain}-${row.action}-${i}`}>
                        <TableCell>
                          <Chip label={actionLabel(row.action)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{row.domain}</TableCell>
                        <TableCell>
                          {row.action === 'without_owner' && row.domainId ? (
                            <AssignOwnerCell
                              domainId={row.domainId}
                              options={customerOptions}
                              disabled={customerOptions.length === 0}
                            />
                          ) : (
                            renderChanges(row)
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
